import { type UploadFile } from "antd/es/upload"
import dayjs, { type Dayjs } from "dayjs"
import React from "react"

import promisify from "./promisify"

const trullyValues: unknown[] = [true, "true", 1]
const falselyValues: unknown[] = [false, "false", 0, undefined, null]

export function removePunctuation (value: string): string {
  return value.replace(/[^\d\w\u00C0-\u00FF]/g, "")
}

export function mimetypeToExtension (value?: string): string {
  switch (value) {
    case ("audio/aac"): return ".aac"
    case ("application/x-abiword"): return ".abw"
    case ("video/x-msvideo"): return ".avi"
    case ("application/vnd.amazon.ebook"): return ".azw"
    case ("application/octet-stream"): return ".bin"
    case ("application/x-bzip"): return ".bz"
    case ("application/x-bzip2"): return ".bz2"
    case ("application/x-csh"): return ".csh"
    case ("text/css"): return ".css"
    case ("text/csv"): return ".csv"
    case ("application/msword"): return ".doc"
    case ("application/vnd.ms-fontobject"): return ".eot"
    case ("application/epub+zip"): return ".epub"
    case ("image/gif"): return ".gif"
    case ("text/html"): return ".html"
    case ("image/x-icon"): return ".ico"
    case ("text/calendar"): return ".ics"
    case ("application/java-archive"): return ".jar"
    case ("image/jpeg"): return ".jpeg"
    case ("application/javascript"): return ".js"
    case ("application/json"): return ".json"
    case ("audio/midi"): return ".midi"
    case ("video/mpeg"): return ".mpeg"
    case ("application/vnd.apple.installer+xml"): return ".mpkg"
    case ("application/vnd.oasis.opendocument.presentation"): return ".odp"
    case ("application/vnd.oasis.opendocument.spreadsheet"): return ".ods"
    case ("application/vnd.oasis.opendocument.text"): return ".odt"
    case ("audio/ogg"): return ".oga"
    case ("video/ogg"): return ".ogv"
    case ("application/ogg"): return ".ogx"
    case ("font/otf"): return ".otf"
    case ("image/png"): return ".png"
    case ("application/pdf"): return ".pdf"
    case ("application/vnd.ms-powerpoint"): return ".ppt"
    case ("application/x-rar-compressed"): return ".rar"
    case ("application/rtf"): return ".rtf"
    case ("application/x-sh"): return ".sh"
    case ("image/svg+xml"): return ".svg"
    case ("application/x-shockwave-flash"): return ".swf"
    case ("application/x-tar"): return ".tar"
    case ("image/tiff"): return ".tiff"
    case ("application/typescript"): return ".ts"
    case ("font/ttf"): return ".ttf"
    case ("application/vnd.visio"): return ".vsd"
    case ("audio/x-wav"): return ".wav"
    case ("audio/webm"): return ".weba"
    case ("video/webm"): return ".webm"
    case ("image/webp"): return ".webp"
    case ("font/woff"): return ".woff"
    case ("font/woff2"): return ".woff2"
    case ("application/xhtml+xml"): return ".xhtml"
    case ("application/xml"): return ".xml"
    case ("application/vnd.mozilla.xul+xml"): return ".xul"
    case ("application/zip"): return ".zip"
    case ("application/x-7z-compressed"): return ".7-zip"

    case ("application/vnd.ms-excel"): return ".xlsx"
    case ("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"): return ".xlsx"

    case ("video/3gpp"): return ".3gp"
    case ("audio/3gpp"): return ".3gp"

    case ("video/3gpp2"): return ".3g2"
    case ("audio/3gpp2"): return ".3g2"

    default: return ""
  }
}

export function toReadableBoolean (value?: boolean): string {
  if (trullyValues.includes(value)) return "Sim"
  else if (falselyValues.includes(value)) return "Não"
  else return "Não"
}

export function toReadableDate (value?: Date | Dayjs): string {
  const formated = dayjs.isDayjs(value) ? value : dayjs(value)

  return formated.format("DD/MM/YYYY HH:mm")
}

export function toCPF (value?: string): string {
  if (!value) return ""

  const formated = value.replace(/\D/g, "").replace(/(\d{0,3})(\d{0,3})?(\d{0,3})?(\d{0,2})?/, "$1.$2.$3-$4")

  return formated
}

export function toLicensePlate (value?: string): string {
  if (!value) return ""

  const formated = value.toUpperCase().replace(/[\W]/g, "").replace(/(\w{0,3})(\w{0,4})?/, "$1-$2")

  return formated
}

export function base64ToBlob (value: string, contentType: string, sliceSize = 512): Blob {
  const byteCharacters = window.atob(value.replace(/^data:\w+\/\w+;\w+,/, ""))
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  const blob = new Blob(byteArrays, { type: contentType })

  return blob
}

export const removeDiacritics = (value: string): string => value.normalize("NFD").replace(/\p{Diacritic}/gu, "")

export const comparableStr = (value?: string): string => {
  if (!value) return ""

  const lowered = String(value).toLowerCase()
  const normalized = removeDiacritics(lowered)
  const noSpecial = normalized.replace(/[^A-Za-z0-9]/g, "")

  return noSpecial
}

export const fileListToBase64 = async function (fileList: UploadFile[]): Promise<Array<{ key: string, base64: string }>> {
  let tempFileList: Array<{ key: string, base64: string }> = []
  for (const file of fileList) {
    const base64 = await promisify.blobTo64(file.originFileObj as Blob)
    tempFileList.push({ key: file.uid, base64 })
    const unique: Array<{ key: string, base64: string }> = Array.from(new Map(tempFileList.map((m) => [m.key, m]))).map(value => value[1])
    tempFileList = unique
  }

  return tempFileList
}

export const UploadFileToBlob = function (fileList?: UploadFile[]) {
  return fileList ? fileList.map(image => image.originFileObj as Blob) : []
}

export const onRequiredTypeChange = (label: React.ReactNode, { required }: { required: boolean }) => {
  if (required) return (<><span style={{ color: "red" }}>*</span>{label}</>)
  else return <>{label} <span style={{ color: "gray" }}>&nbsp;(Opcional)</span></>
}
